import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ScanSearch, Sparkles, Loader2 } from "lucide-react";

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [seedDemo, setSeedDemo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "error") {
      toast.error("La connexion Google a échoué. Réessayez ou utilisez votre e-mail.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password, seedDemo);
      toast.success(
        mode === "login"
          ? "Bienvenue !"
          : seedDemo
          ? "Compte créé — vos contrats démo sont prêts"
          : "Compte créé — ajoutez vos premiers contrats"
      );
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setMode("login");
    setEmail("demo@iacover.fr");
    setPassword("demo1234");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/google/native/login`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground overflow-hidden grain-bg">
        <div className="relative z-10">
          <img src="/logo-mark-light.png" alt="IACover" className="h-9 w-auto" />
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary-foreground/60 mt-2">Audit IA & Prévoyance</p>
        </div>

        <div className="relative z-10 space-y-8 max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
            Tous vos contrats.<br />Une seule lecture claire.
          </h1>
          <ul className="space-y-4 text-primary-foreground/80">
            <li className="flex gap-3">
              <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-ochre" />
              <span className="text-sm leading-relaxed">Déposez vos PDF, l'IA détecte franchises, plafonds et exclusions.</span>
            </li>
            <li className="flex gap-3">
              <Sparkles className="w-5 h-5 shrink-0 mt-0.5 text-ochre" />
              <span className="text-sm leading-relaxed">Repérez les doublons coûteux et les trous dans votre couverture.</span>
            </li>
            <li className="flex gap-3">
              <ScanSearch className="w-5 h-5 shrink-0 mt-0.5 text-ochre" />
              <span className="text-sm leading-relaxed">« J'ai cassé mon écran, suis-je couvert ? » — demandez à l'assistant.</span>
            </li>
          </ul>
        </div>

        <p className="relative z-10 text-xs font-mono text-primary-foreground/40">© 2026 Iacover</p>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-primary-foreground/5 blur-2xl" />
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-in-up">
          <div className="lg:hidden mb-8">
            <img src="/logo-mark.png" alt="IACover" className="h-8 w-auto" />
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {mode === "login" ? "Content de vous revoir" : "Créer votre compte"}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {mode === "login"
              ? "Connectez-vous pour retrouver votre audit d'assurance."
              : "Quelques secondes suffisent. Des contrats de démo vous attendent."}
          </p>

          <div className="mt-6 inline-flex p-1 rounded-full bg-muted text-sm font-medium">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-mode-toggle-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`px-4 py-1.5 rounded-full transition-colors ${
                  mode === m ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "login" ? "Connexion" : "Créer un compte"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium">Nom complet</label>
                <input
                  data-testid="auth-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@email.fr"
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Mot de passe</label>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition"
              />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-destructive font-medium">{error}</p>}

            {mode === "register" && (
              <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl border border-border bg-card px-3.5 py-3">
                <input
                  data-testid="auth-seed-demo-checkbox"
                  type="checkbox"
                  checked={seedDemo}
                  onChange={(e) => setSeedDemo(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary shrink-0"
                />
                <span className="text-sm">
                  Démarrer avec des exemples de contrats
                  <span className="block text-xs text-muted-foreground mt-0.5">Décochez pour un compte vide et importer vos vrais contrats.</span>
                </span>
              </label>
            )}

            <button
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 active:scale-[0.99] transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>

          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">ou</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full h-11 rounded-xl border border-input bg-card text-sm font-medium hover:bg-muted active:scale-[0.99] transition flex items-center justify-center gap-2.5"
          >
            <GoogleIcon /> Se connecter avec Google
          </button>

          <button
            data-testid="auth-demo-btn"
            onClick={fillDemo}
            className="mt-4 w-full text-xs text-muted-foreground hover:text-foreground transition font-mono"
          >
            Utiliser le compte démo → demo@iacover.fr
          </button>
        </div>
      </div>
    </div>
  );
}
